import { useContext, useEffect, useReducer } from "react";
import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import ReactMarkdown from "react-markdown";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import {
  generateDraftMessage,
  type DraftMessageOutput,
} from "./draftMessageService";
import type { DraftTone, DraftLength } from "./DraftMessagePanel";
import { PillGroup, TONE_OPTIONS, LENGTH_OPTIONS } from "./DraftPillGroup";
import DraftResponseView, { DraftAdjustFooter } from "./DraftResponseView";
import "./AiDraftMessage.scss";

export interface MessagePair {
  id: string;
  question: string;
  response: string;
}

const DISALLOWED_MARKDOWN_ELEMENTS = [
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "blockquote",
];

interface DraftConversationPanelProps {
  messagePairs: MessagePair[];
  onResultStateChange?: (hasResult: boolean) => void;
  resetSignal?: number;
}

// ── Reducer ────────────────────────────────────────────────────────────────────

type Action =
  | { type: "setTone"; tone: DraftTone }
  | { type: "setLength"; length: DraftLength }
  | { type: "toggleId"; id: string }
  | { type: "selectAll"; allIds: string[] }
  | { type: "toggleExpand"; id: string }
  | { type: "generateStart" }
  | { type: "generateSuccess"; output: DraftMessageOutput }
  | { type: "generateError"; error: string }
  | { type: "adjustStart" }
  | { type: "adjustSuccess"; output: DraftMessageOutput }
  | { type: "adjustError"; error: string }
  | { type: "resetToSelection" };

interface State {
  tone: DraftTone;
  length: DraftLength;
  selectedIds: string[];
  isGenerating: boolean;
  error: string | null;
  draftOutput: DraftMessageOutput | null;
  expandedId: string | null;
  isAdjusting: boolean;
  adjustError: string | null;
}

function createInitialState(messagePairs: MessagePair[]): State {
  return {
    tone: "professional",
    length: "standard",
    selectedIds: messagePairs.map((p) => p.id),
    isGenerating: false,
    error: null,
    draftOutput: null,
    expandedId: null,
    isAdjusting: false,
    adjustError: null,
  };
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setTone":
      return { ...state, tone: action.tone };
    case "setLength":
      return { ...state, length: action.length };
    case "toggleId": {
      const isSelected = state.selectedIds.includes(action.id);
      return {
        ...state,
        selectedIds: isSelected
          ? state.selectedIds.filter((id) => id !== action.id)
          : [...state.selectedIds, action.id],
      };
    }
    case "selectAll":
      return { ...state, selectedIds: action.allIds };
    case "toggleExpand":
      return {
        ...state,
        expandedId: state.expandedId === action.id ? null : action.id,
      };
    case "generateStart":
      return { ...state, isGenerating: true, error: null };
    case "generateSuccess":
      return { ...state, isGenerating: false, draftOutput: action.output };
    case "generateError":
      return { ...state, isGenerating: false, error: action.error };
    case "adjustStart":
      return { ...state, isAdjusting: true, adjustError: null };
    case "adjustSuccess":
      return { ...state, isAdjusting: false, draftOutput: action.output };
    case "adjustError":
      return { ...state, isAdjusting: false, adjustError: action.error };
    case "resetToSelection":
      return { ...state, draftOutput: null, error: null, adjustError: null };
    default:
      return state;
  }
};

// ── Main component ─────────────────────────────────────────────────────────────

function DraftConversationPanel({
  messagePairs,
  onResultStateChange,
  resetSignal,
}: DraftConversationPanelProps) {
  const intl = useIntl();
  const { isAiAssistantAvailable } = useContext<AppContextInterface>(AppContext);
  const isAiEnabled = isAiAssistantAvailable;
  const [state, dispatch] = useReducer(
    reducer,
    messagePairs,
    createInitialState,
  );
  const {
    tone,
    length,
    selectedIds,
    isGenerating,
    error,
    draftOutput,
    expandedId,
    isAdjusting,
    adjustError,
  } = state;

  useEffect(() => {
    onResultStateChange?.(!!draftOutput);
  }, [draftOutput, onResultStateChange]);

  useEffect(() => {
    if (resetSignal !== undefined) dispatch({ type: "resetToSelection" });
  }, [resetSignal]);

  const hasNoInsights = messagePairs.length === 0;
  const hasNoSelection = !hasNoInsights && selectedIds.length === 0;

  const handleGenerate = async () => {
    if (hasNoSelection || !isAiEnabled) return;

    const selectedMessages = messagePairs
      .filter((p) => selectedIds.includes(p.id))
      .map((p) => p.response);

    dispatch({ type: "generateStart" });
    try {
      const output = await generateDraftMessage({
        inputMessage: selectedMessages,
        tone,
        length,
      });
      dispatch({ type: "generateSuccess", output });
    } catch {
      dispatch({
        type: "generateError",
        error: intl.formatMessage({ id: "app.toolboxChat.draftConversation.error" }),
      });
    }
  };

  const handleAdjust = async (adjustment: string): Promise<boolean> => {
    if (!draftOutput || !isAiEnabled) return false;

    dispatch({ type: "adjustStart" });
    try {
      const output = await generateDraftMessage({
        currentOutput: draftOutput,
        userPrompt: adjustment,
      });
      dispatch({ type: "adjustSuccess", output });
      return true;
    } catch {
      dispatch({
        type: "adjustError",
        error: intl.formatMessage({ id: "app.toolboxChat.draftResponse.adjust.error" }),
      });
      return false;
    }
  };

  if (isGenerating) {
    return (
      <div className="dcp dcp--loading">
        <img
          src="https://cdn.watermarkinsights.com/images/loaders/ai-pulsing-dot-loader.svg"
          alt={intl.formatMessage({ id: "app.toolboxChat.draftConversation.loading" })}
          className="draft-loader-img"
        />
        <span className="draft-loader-text">
          {intl.formatMessage({ id: "app.toolboxChat.draftConversation.loading" })}
        </span>
      </div>
    );
  }

  if (draftOutput) {
    return (
      <>
        <div className="dcp dcp--result">
          {isAdjusting ? (
            <div className="dcp__adjust-loading">
              <img
                src="https://cdn.watermarkinsights.com/images/loaders/ai-pulsing-dot-loader.svg"
                alt={intl.formatMessage({ id: "app.toolboxChat.draftConversation.adjustLoading" })}
                className="draft-loader-img"
              />
              <span className="draft-loader-text">
                {intl.formatMessage({ id: "app.toolboxChat.draftConversation.adjustLoading" })}
              </span>
            </div>
          ) : (
            <DraftResponseView output={draftOutput} />
          )}
        </div>
        <DraftAdjustFooter
          onAdjust={handleAdjust}
          isAdjusting={isAdjusting}
          adjustError={adjustError}
        />
      </>
    );
  }

  if (hasNoInsights) {
    return (
      <div className="dcp">
        <div className="dcp__empty">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" className="dcp__empty-icon">
            <path d="M10.4993 16.5424V18.834H12.7909L19.5498 12.0752L17.2582 9.78349L10.4993 16.5424ZM21.322 10.3029C21.5604 10.0646 21.5604 9.6796 21.322 9.44127L19.892 8.01127C19.6537 7.77294 19.2687 7.77294 19.0304 8.01127L17.912 9.1296L20.2037 11.4213L21.322 10.3029Z" fill="currentColor"/>
            <path d="M26.6667 2.66675H5.33341C3.86675 2.66675 2.66675 3.86675 2.66675 5.33341V29.3334L8.00008 24.0001H26.6667C28.1334 24.0001 29.3334 22.8001 29.3334 21.3334V5.33341C29.3334 3.86675 28.1334 2.66675 26.6667 2.66675ZM26.6667 21.3334H8.00008L5.33341 24.0001V5.33341H26.6667V21.3334Z" fill="currentColor"/>
          </svg>
          <p className="dcp__empty-text">
            {intl.formatMessage({ id: "app.toolboxChat.draftConversation.noInsights" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dcp">
        <p className="draft-intro">
          {intl.formatMessage({ id: "app.toolboxChat.draftConversation.intro" })}
        </p>

        <section className="draft-section">
          <p className="draft-section-label" id="dcp-tone-label">
            {intl.formatMessage({ id: "app.toolboxChat.draftMessage.toneLabel" })}
          </p>
          <PillGroup
            options={TONE_OPTIONS.map(({ value, messageId }) => ({
              value,
              label: intl.formatMessage({ id: messageId }),
            }))}
            selected={tone}
            name="dcp-tone"
            labelId="dcp-tone-label"
            onSelect={(value) => dispatch({ type: "setTone", tone: value })}
          />
        </section>

        <section className="draft-section">
          <p className="draft-section-label" id="dcp-length-label">
            {intl.formatMessage({ id: "app.toolboxChat.draftMessage.lengthLabel" })}
          </p>
          <PillGroup
            options={LENGTH_OPTIONS.map(({ value, messageId }) => ({
              value,
              label: intl.formatMessage({ id: messageId }),
            }))}
            selected={length}
            name="dcp-length"
            labelId="dcp-length-label"
            onSelect={(value) => dispatch({ type: "setLength", length: value })}
          />
        </section>

        <section className="draft-section">
          <div className="dcp__source-header">
            <p
              className={`dcp__source-label${hasNoSelection ? " dcp__source-label--error" : ""}`}
              id="dcp-source-label"
            >
              {intl.formatMessage({ id: "app.toolboxChat.draftConversation.sourceLabel" })}
              <span className="dcp__source-count">
                {intl.formatMessage(
                  { id: "app.toolboxChat.draftConversation.sourceLabelCount" },
                  { count: selectedIds.length },
                )}
              </span>
              {hasNoSelection && (
                <span className="dcp__source-warning" aria-hidden="true">⚠</span>
              )}
            </p>
            {messagePairs.length > 1 && <label className="dcp__select-all">
              <input
                type="checkbox"
                className="dcp__card-checkbox"
                checked={selectedIds.length === messagePairs.length}
                onChange={() => {
                  const allIds = selectedIds.length === messagePairs.length
                    ? []
                    : messagePairs.map((p) => p.id);
                  dispatch({ type: "selectAll", allIds });
                }}
              />
              {intl.formatMessage({ id: "app.toolboxChat.draftConversation.selectAll" })}
            </label>}
          </div>
          {hasNoSelection && (
            <p className="dcp__no-selection-msg">
              {intl.formatMessage({ id: "app.toolboxChat.draftConversation.noSelection" })}
            </p>
          )}

          <ul className="dcp__card-list" aria-labelledby="dcp-source-label">
            {messagePairs.map((pair) => {
              const isSelected = selectedIds.includes(pair.id);
              const isExpanded = expandedId === pair.id;
              return (
                <li key={pair.id} className="dcp__card-row">
                  {messagePairs.length > 1 && (
                    <input
                      type="checkbox"
                      className="dcp__card-checkbox"
                      checked={isSelected}
                      onChange={() => dispatch({ type: "toggleId", id: pair.id })}
                      aria-label={pair.question || pair.response.slice(0, 60)}
                    />
                  )}
                  <div className={`dcp__card${isSelected ? " dcp__card--selected" : ""}`}>
                    <div className="dcp__card-body">
                      {pair.question && (
                        <p className="dcp__card-title">{pair.question}</p>
                      )}
                      <div className={`dcp__card-preview${isExpanded ? " dcp__card-preview--expanded" : ""}`}>
                        <ReactMarkdown
                          disallowedElements={DISALLOWED_MARKDOWN_ELEMENTS}
                          unwrapDisallowed
                        >
                          {pair.response}
                        </ReactMarkdown>
                      </div>
                      <WmButton
                        buttonType="textonly"
                        className="dcp__card-toggle"
                        onClick={() => dispatch({ type: "toggleExpand", id: pair.id })}
                      >
                        {intl.formatMessage({
                          id: isExpanded
                            ? "app.toolboxChat.draftConversation.hideFullResponse"
                            : "app.toolboxChat.draftConversation.showFullResponse",
                        })}
                      </WmButton>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {error && <p className="draft-error">{error}</p>}
      </div>

      <div className="dcp__footer">
        {isAiEnabled && (
          <WmButton
            buttonType="primary-ai"
            onClick={handleGenerate}
            disabled={hasNoSelection}
          >
            {intl.formatMessage({ id: "app.toolboxChat.draftMessage.action.generate" })}
          </WmButton>
        )}
      </div>
    </>
  );
}

export default DraftConversationPanel;
